import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { IndexColumn } from "components/common/columns";
import { DateOnly } from "components/common/Date";
import { formatQuantity } from "utils/quantity";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { MaterialInOutSourceType } from "features/MaterialInOuts/constants/material-in-out.constants";
import { MaterialInOut } from "features/MaterialInOuts/types/material-in-out.types";
import {
  deleteMaterialInOut,
  getMaterialInOuts,
  returnMaterialInOut,
} from "features/MaterialInOuts/services/MaterialInOutService";
import { MaterialInOutCreateModal } from "../MaterialInOutForm/MaterialInOutCreateModal";
import MaterialInOutListFilters from "./MaterialInOutListFilters";

function concernedPersonName(row: MaterialInOut): string {
  const { firstName, lastName } = row.user ?? {};
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "—";
}

function WorkCell({ row }: { row: MaterialInOut }) {
  const isOther = row.sourceType === MaterialInOutSourceType.OTHER;
  const label =
    (isOther ? row.otherDescription : row.workLabel)?.trim() || row.workLabel || "—";

  if (!isOther && row.sourceId != null) {
    const base = SOURCE_TYPE_URL_MAP[String(row.sourceType) as AnySourceType];
    if (base) {
      return (
        <Link to={`${base}/${row.sourceId}`} className="text-decoration-none link">
          {label}
        </Link>
      );
    }
  }

  return <span>{label}</span>;
}

function StatusCell({ row }: { row: MaterialInOut }) {
  if (row.dateIn == null) {
    return <span className="badge bg-warning-subtle text-warning">OUT</span>;
  }
  return (
    <div className="d-flex flex-column align-items-start gap-1">
      <span className="badge bg-success-subtle text-success">RETURNED</span>
      <DateOnly date={row.dateIn} />
    </div>
  );
}

const MaterialInOutList = () => {
  document.title = getDocumentTitle("Material In/Out");
  const { can } = useAccess();
  const { isSuperAdmin } = useProfile();

  const canCreate = can(Permissions.MATERIAL_IN_OUT.CREATE);
  const canUpdate = can(Permissions.MATERIAL_IN_OUT.UPDATE);
  const canReturn = can(Permissions.MATERIAL_IN_OUT.RETURN);
  const canDelete = can(Permissions.MATERIAL_IN_OUT.DELETE);

  const tableRef = useRef<() => void>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [selected, setSelected] = useState<MaterialInOut | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [sourceType, setSourceType] = useState<MaterialInOutSourceType | "">("");
  const [materialId, setMaterialId] = useState<number | null>(null);
  const [materialOption, setMaterialOption] = useState<
    { value: number; label: string } | undefined
  >(undefined);
  const [userId, setUserId] = useState<number | null>(null);
  const [userOption, setUserOption] = useState<
    { value: number; label: string } | undefined
  >(undefined);

  const requestParams = useMemo(
    () => ({
      outstandingOnly: outstandingOnly || undefined,
      sourceType: sourceType || undefined,
      materialId: materialId ?? undefined,
      userId: userId ?? undefined,
    }),
    [outstandingOnly, sourceType, materialId, userId],
  );

  const filterCount = [
    outstandingOnly,
    sourceType,
    materialId,
    userId,
  ].filter((v) => v != null && v !== "" && v !== false).length;

  const clearFilters = useCallback(() => {
    setOutstandingOnly(false);
    setSourceType("");
    setMaterialId(null);
    setMaterialOption(undefined);
    setUserId(null);
    setUserOption(undefined);
  }, []);

  const openCreate = useCallback(() => setCreateModalOpen(true), []);
  const closeCreateModal = useCallback(() => setCreateModalOpen(false), []);

  const onClickDelete = useCallback((record: MaterialInOut) => {
    setSelected(record);
    setDeleteOpen(true);
  }, []);

  const onClickReturn = useCallback((record: MaterialInOut) => {
    setSelected(record);
    setReturnOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteMaterialInOut(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Could not delete record.",
      );
    } finally {
      setDeleteOpen(false);
      setSelected(null);
    }
  };

  const handleReturn = async () => {
    if (!selected) return;
    setReturnSubmitting(true);
    try {
      const res = await returnMaterialInOut(selected.id, {});
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
        setReturnOpen(false);
        setSelected(null);
      } else {
        toast.error(res.message ?? "Could not mark as returned.");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Could not mark as returned.",
      );
    } finally {
      setReturnSubmitting(false);
    }
  };

  const columns: ColumnDef<MaterialInOut>[] = useMemo(
    () => [
      IndexColumn<MaterialInOut>(),
      {
        header: "Material",
        accessorKey: "material",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.material?.name ?? "—"}</span>
        ),
      },
      {
        header: "Concerned Person",
        id: "employee",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => concernedPersonName(row.original),
      },
      {
        header: "Work",
        id: "work",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => <WorkCell row={row.original} />,
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatQuantity(row.original.quantity),
      },
      {
        header: "Date Out",
        accessorKey: "dateOut",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => <DateOnly date={row.original.dateOut} />,
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => <StatusCell row={row.original} />,
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isOpen = r.dateIn == null;
          const showEdit = canUpdate && (isOpen || isSuperAdmin);
          const showReturn = canReturn && isOpen;
          const hasMenuItems = showEdit || showReturn || canDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Material in/out actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {showEdit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/inventory-management/material-in-outs/edit/${r.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showReturn ? (
                  <DropdownItem onClick={() => onClickReturn(r)}>
                    <i className="ri-arrow-go-back-line align-bottom me-2 text-success" />
                    Mark Returned
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {showEdit || showReturn ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(r)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canUpdate, canReturn, canDelete, isSuperAdmin, onClickDelete, onClickReturn],
  );

  return (
    <React.Fragment>
      <MaterialInOutCreateModal
        show={createModalOpen}
        editingRecord={null}
        onClose={closeCreateModal}
        onSuccess={() => tableRef.current?.()}
      />

      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.material?.name}
      />

      <ConfirmActionModal
        show={returnOpen}
        title="Mark as returned"
        iconClass="ri-arrow-go-back-line"
        iconColor="text-success"
        heading="Mark this material as returned?"
        description={
          selected
            ? `${selected.material?.name ?? "This material"} (qty ${selected.quantity}) will be marked as returned as of now.`
            : undefined
        }
        confirmText="Mark returned"
        confirmColor="success"
        confirmDisabled={returnSubmitting}
        onConfirm={() => void handleReturn()}
        onClose={() => {
          setReturnOpen(false);
          setSelected(null);
        }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Material In/Out" pageTitle="Inventory Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Material Check-out Ledger</h5>
                <p className="text-muted small mb-0">
                  Track materials checked out against Work Orders, Petty
                  Works, AMCs, or other purposes, and mark them returned when
                  the material comes back.
                </p>
              </div>
              {canCreate ? (
                <button type="button" className="btn btn-primary" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  Add
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getMaterialInOuts}
                requestParams={requestParams}
                pageSize={10}
                globalSearch
                SearchPlaceholder="Search by material, work, or description…"
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                filterCount={filterCount}
                onFiltersClear={clearFilters}
                toolbarRight={
                  <MaterialInOutListFilters
                    outstandingOnly={outstandingOnly}
                    onOutstandingOnlyChange={setOutstandingOnly}
                    sourceType={sourceType}
                    onSourceTypeChange={setSourceType}
                    materialId={materialId}
                    materialOption={materialOption}
                    onMaterialChange={(id, opt) => {
                      setMaterialId(id);
                      setMaterialOption(opt);
                    }}
                    userId={userId}
                    userOption={userOption}
                    onUserChange={(id, opt) => {
                      setUserId(id);
                      setUserOption(opt);
                    }}
                  />
                }
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MaterialInOutList;
